import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import DateTimeHeader from "../components/DateTimeHeader";
import Dropdown from "../components/Dropdown";
import {
  Search,
  Circle,
  X,
  Users,
  Building,
  Briefcase,
  Filter,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Award,
  Star,
  UserPlus,
  MoreHorizontal,
  Moon,
  Sun,
  ChevronDown,
  Grid3X3,
  List,
  Loader2,
  Wifi,
  WifiOff,
  Clock,
  RefreshCw,
  Bell,
  CalendarCheck,
  AlertCircle,
  Check,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";


// Enhanced mock data with more realistic information
const mockUsers = [
  {
    id: 1,
    name: "Ledy Sintia",
    email: "ledy.sintia@vela.com",
    role: "Senior Software Engineer",
    avatar: "https://randomuser.me/api/portraits/women/44.jpg",
    department: "Engineering",
    status: "online",
    lastSeen: new Date(),
    bio: "Passionate about building scalable web applications and creating innovative solutions. Always eager to learn new technologies and contribute to meaningful projects.",
    skills: ["React", "TypeScript", "Node.js", "Python", "AWS", "Docker"],
    location: "San Francisco, CA",
    joinDate: "January 2023",
    phone: "+1 (555) 123-4567",
    timezone: "PST",
    projects: 12,
    connections: 45,
  },
  {
    id: 2,
    name: "John Doe",
    email: "john.doe@vela.com",
    role: "Product Manager",
    avatar: "https://randomuser.me/api/portraits/men/32.jpg",
    department: "Product",
    status: "offline",
    lastSeen: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 hours ago
    bio: "Product enthusiast and agile advocate with 5+ years of experience in product strategy and team leadership.",
    skills: [
      "Agile",
      "Scrum",
      "Roadmapping",
      "Analytics",
      "User Research",
      "A/B Testing",
    ],
    location: "New York, NY",
    joinDate: "March 2022",
    phone: "+1 (555) 234-5678",
    timezone: "EST",
    projects: 8,
    connections: 32,
  },
  {
    id: 3,
    name: "Jane Smith",
    email: "jane.smith@vela.com",
    role: "UI/UX Designer",
    avatar: "https://randomuser.me/api/portraits/women/65.jpg",
    department: "Design",
    status: "away",
    lastSeen: new Date(Date.now() - 15 * 60 * 1000), // 15 minutes ago
    bio: "Designing delightful user experiences that make a difference. Specialized in user research and interaction design.",
    skills: [
      "Figma",
      "Sketch",
      "Prototyping",
      "User Research",
      "Design Systems",
      "Accessibility",
    ],
    location: "Austin, TX",
    joinDate: "June 2023",
    phone: "+1 (555) 345-6789",
    timezone: "CST",
    projects: 15,
    connections: 28,
  },
  {
    id: 4,
    name: "Ahmed Ali",
    email: "ahmed.ali@vela.com",
    role: "QA Engineer",
    avatar: "https://randomuser.me/api/portraits/men/45.jpg",
    department: "Engineering",
    status: "online",
    lastSeen: new Date(),
    bio: "Ensuring quality in every release through comprehensive testing strategies and automation.",
    skills: [
      "Testing",
      "Automation",
      "Cypress",
      "Selenium",
      "Performance Testing",
      "Jest",
    ],
    location: "Seattle, WA",
    joinDate: "September 2022",
    phone: "+1 (555) 456-7890",
    timezone: "PST",
    projects: 9,
    connections: 38,
  },
  {
    id: 5,
    name: "Maria Garcia",
    email: "maria.garcia@vela.com",
    role: "HR Specialist",
    avatar: "https://randomuser.me/api/portraits/women/68.jpg",
    department: "HR",
    status: "offline",
    lastSeen: new Date(Date.now() - 4 * 60 * 60 * 1000), // 4 hours ago
    bio: "People-first HR specialist focused on creating inclusive workplace cultures and supporting employee growth.",
    skills: [
      "Recruiting",
      "Onboarding",
      "Benefits",
      "Employee Relations",
      "Diversity & Inclusion",
      "HRIS",
    ],
    location: "Miami, FL",
    joinDate: "November 2022",
    phone: "+1 (555) 567-8901",
    timezone: "EST",
    projects: 6,
    connections: 52,
  },
  {
    id: 6,
    name: "Bryan Lee",
    email: "bryan.lee@vela.com",
    role: "DevOps Engineer",
    avatar: "https://randomuser.me/api/portraits/men/50.jpg",
    department: "Engineering",
    status: "online",
    lastSeen: new Date(),
    bio: "Automating all the things and building robust infrastructure solutions for scalable applications.",
    skills: ["AWS", "Docker", "CI/CD", "Kubernetes", "Terraform", "Monitoring"],
    location: "Denver, CO",
    joinDate: "April 2023",
    phone: "+1 (555) 678-9012",
    timezone: "MST",
    projects: 11,
    connections: 41,
  },
  {
    id: 7,
    name: "Sarah Johnson",
    email: "sarah.johnson@vela.com",
    role: "Marketing Manager",
    avatar: "https://randomuser.me/api/portraits/women/22.jpg",
    department: "Marketing",
    status: "away",
    lastSeen: new Date(Date.now() - 30 * 60 * 1000), // 30 minutes ago
    bio: "Creative marketing strategist with expertise in digital campaigns and brand development.",
    skills: [
      "Digital Marketing",
      "SEO",
      "Social Media",
      "Content Strategy",
      "Analytics",
      "Branding",
    ],
    location: "Chicago, IL",
    joinDate: "February 2023",
    phone: "+1 (555) 789-0123",
    timezone: "CST",
    projects: 7,
    connections: 35,
  },
  {
    id: 8,
    name: "David Chen",
    email: "david.chen@vela.com",
    role: "Data Scientist",
    avatar: "https://randomuser.me/api/portraits/men/67.jpg",
    department: "Data",
    status: "online",
    lastSeen: new Date(),
    bio: "Turning data into insights and building machine learning models that drive business decisions.",
    skills: [
      "Python",
      "R",
      "SQL",
      "Machine Learning",
      "Data Visualization",
      "Statistics",
    ],
    location: "Boston, MA",
    joinDate: "May 2023",
    phone: "+1 (555) 890-1234",
    timezone: "EST",
    projects: 14,
    connections: 29,
  },
];



const statusConfig = {
  online: {
    color: "bg-green-500",
    label: "Online",
    icon: Wifi,
    description: "Active now",
  },
  offline: {
    color: "bg-gray-400",
    label: "Offline",
    icon: WifiOff,
    description: "Last seen",
  },
  away: {
    color: "bg-yellow-400",
    label: "Away",
    icon: Clock,
    description: "Away",
  },
};
interface DepartmentDropdownProps {
  department: string;
  setDepartment: (value: string) => void;
  darkMode: boolean;
  departments: string[]; 
}

const DepartmentDropdown = ({ department, setDepartment, darkMode, departments }: DepartmentDropdownProps) => {
  const [open, setOpen] = useState(false);

  const options = ["", ...departments];

  const formatDept = (d: string) =>
    d
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");

  return (
    <div className="relative min-w-[180px]">
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between rounded-xl py-5 pl-10 pr-4 border text-xs
          ${darkMode ? "bg-white/10 border-white/20 text-white" : "bg-gray-50 border-gray-300 text-gray-900"}
        `}
      >
        <span>{department === "" ? "All Departments" : formatDept(department)}</span>
        <ChevronDown size={14} className="opacity-70" />
      </button>

      {open && (
        <div
          className={`absolute top-[105%] left-0 w-full z-[999] rounded-xl shadow-xl border max-h-60 overflow-auto
            ${darkMode ? "bg-[#1c1c29] border-white/10" : "bg-white border-gray-200"}
          `}
        >
          {options.map((opt, i) => (
            <div
              key={i}
              onClick={() => {
                setDepartment(opt);
                setOpen(false);
              }}
              className={`px-4 py-3 cursor-pointer flex items-center justify-between text-xs
                ${darkMode ? "text-white hover:bg-white/10" : "text-gray-800 hover:bg-gray-100"}
              `}
            >
              <span>{opt === "" ? "All Departments" : formatDept(opt)}</span>
              {department.toLowerCase() === opt.toLowerCase() && <Check size={14} className="text-blue-500" />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Community: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("");
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isLoading, setIsLoading] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { getAllCommunityUsers, communityUsers } = useAuth();
  console.log(selectedUser,"selectedUserselectedUserselectedUserselectedUserselectedUser")

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vela-back.wingtrill.com';

  const allDepartments = useMemo(() => {
  const map = new Map<string, string>();

  communityUsers.forEach((u) => {
    const dept = u.department?.trim();
    if (dept) {
      const key = dept.toLowerCase();
      if (!map.has(key)) {
        map.set(key, dept);
      }
    }
  });

  return Array.from(map.values());
}, [communityUsers]);

  // Notification state variables
  const initialNotifications = [
    {
      id: 1,
      icon: <CalendarCheck className="text-blue-400" size={18} />,
      text: "Meeting with Alex at 2:00 PM",
    },
    {
      id: 2,
      icon: <CalendarCheck className="text-green-400" size={18} />,
      text: "Design Review scheduled for tomorrow",
    },
    {
      id: 3,
      icon: <AlertCircle className="text-yellow-400" size={18} />,
      text: "System update available",
    },
    {
      id: 4,
      icon: <AlertCircle className="text-red-400" size={18} />,
      text: "Your password will expire soon",
    },
  ];
  const [notifications, setNotifications] = useState(initialNotifications);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");

  // Simulate real-time online status updates
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineUsers(
        new Set(
          mockUsers
            .filter((user) => user.status === "online")
            .map((user) => user.id)
        )
      );
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Debounced search
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (value: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setSearch(value), 300);
      };
    })(),
    []
  );

  // Filtered and sorted users
  const filteredUsers = useMemo(() => {
    let filtered = communityUsers.filter(
      (u) =>
        (department === "" || u.department === department) &&
        (u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()) ||
          u.job_title?.toLowerCase().includes(search.toLowerCase()) ||
          u.department?.toLowerCase().includes(search.toLowerCase()))
    );

    return filtered;
  }, [search, department, communityUsers]);

  // useEffect for fetching users (re-enabled, assuming it's needed)
  useEffect(() => {
    getAllCommunityUsers().then(() => {
      // Logic after fetching
    });
  }, []);

  // Format last seen time
  const formatLastSeen = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <div
      className={`min-h-screen ${darkMode
        ? "bg-gradient-to-br from-[#181824] to-[#232336]"
        : "bg-gradient-to-br from-gray-50 to-blue-50"
        }`}
    >
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <main
        className={`flex-1 px-6 py-8 md:px-12 md:py-10 transition-all duration-200 ${collapsed ? "ml-20" : "ml-20 md:ml-52"
          }`}
      >
        {/* Top Bar */}
        <NavBar />

        {/* Page Title */}
        <div className="mb-8 mt-12">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
            <div>
              <h2
                className={`text-xl font-bold ${darkMode ? "text-white" : "text-gray-900"
                  }`}
              >
                Community
              </h2>
            </div>
          </div>
        </div>

        {/* Sticky Search and Filters */}
        <div
          className={`sticky top-0 z-10 backdrop-blur-lg rounded-2xl p-6 shadow-lg border mb-8 ${darkMode
            ? "bg-white/5 border-white/10"
            : "bg-white/80 border-gray-100"
            }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative group">
              <input
                type="text"
                onChange={(e) => debouncedSearch(e.target.value)}
                placeholder="Search users by name, email, role, or department..."
                className={`w-full border rounded-xl py-4 pl-12 pr-4 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${darkMode
                  ? "bg-white/10 border-white/20 text-white placeholder-white/50"
                  : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500"
                  }`}
              />
              <Search
                className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 group-focus-within:text-pink-500 ${darkMode ? "text-white/50" : "text-gray-400"
                  }`}
                size={20}
              />
            </div>

            {/* Department Filter */}
            <div className="relative">
              <DepartmentDropdown
                department={department}
                setDepartment={setDepartment}
                departments={allDepartments}
                darkMode={darkMode}
              />

              <Filter
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? "text-white/60" : "text-gray-600"
                  }`}
                size={14}
              />
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex items-center justify-between mb-6">
          {/* Community Count */}
          <div
            className={`flex items-center gap-2 rounded-xl p-1 ${darkMode ? "bg-white/10" : "bg-gray-100"
              }`}
          >
            <div
              className={`px-3 py-2 text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                }`}
            >
              {filteredUsers.length}{" "}
              {filteredUsers.length === 1 ? "member" : "members"}
            </div>
          </div>

          {/* View Mode Toggle */}
          <div
            className={`flex items-center gap-2 rounded-xl p-1 ${darkMode ? "bg-white/10" : "bg-gray-100"
              }`}
          >
            <button
              className={`p-2 rounded-lg transition-all duration-200 ${viewMode === "grid"
                ? `${darkMode
                  ? "bg-white/20 text-pink-500"
                  : "bg-white text-pink-600"
                } shadow-sm`
                : `${darkMode
                  ? "text-white/60 hover:text-pink-400"
                  : "text-gray-600 hover:text-pink-600"
                }`
                }`}
              onClick={() => setViewMode("grid")}
              aria-label="Grid view"
            >
              <Grid3X3 size={18} />
            </button>
            <button
              className={`p-2 rounded-lg transition-all duration-200 ${viewMode === "list"
                ? `${darkMode
                  ? "bg-white/20 text-pink-500"
                  : "bg-white text-pink-600"
                } shadow-sm`
                : `${darkMode
                  ? "text-white/60 hover:text-pink-400"
                  : "text-gray-600 hover:text-pink-600"
                }`
                }`}
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* User Cards */}
        {filteredUsers.length === 0 ? (
          <div className="text-center py-16">
            <div
              className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${darkMode ? "bg-white/10" : "bg-gray-100"
                }`}
            >
              <Search
                size={32}
                className={`${darkMode ? "text-white/40" : "text-gray-400"}`}
              />
            </div>
            <h3
              className={`text-xl font-semibold mb-2 ${darkMode ? "text-white" : "text-gray-900"
                }`}
            >
              No users found
            </h3>
            <p className={`${darkMode ? "text-white/60" : "text-gray-600"}`}>
              Try adjusting your search criteria or filters
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8"
                : "space-y-3"
            }
          >
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                className={`group relative overflow-hidden ${viewMode === "list"
                  ? `flex items-center rounded-2xl p-4 hover:shadow-lg transition-all duration-300 cursor-pointer ${darkMode
                    ? "bg-gradient-to-br from-white/5 to-white/2 border-white/10 hover:border-white/20 hover:bg-white/10"
                    : "bg-white border-gray-200/60 hover:border-gray-300 hover:shadow-lg"
                  }`
                  : `rounded-2xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer ${darkMode
                    ? "bg-gradient-to-br from-white/5 to-white/2 border-white/10 hover:border-white/20 hover:bg-white/10"
                    : "bg-white border-gray-200/60 hover:border-gray-300 hover:shadow-lg"
                  }`
                  }`}
                onClick={() => setSelectedUser(user)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && setSelectedUser(user)}
              >
                {/* Subtle background accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pink-500 to-red-500"></div>

                {/* Grid View Layout */}
                {viewMode === "grid" && (
                  <>
                    {/* Avatar */}
                    <div className="relative mb-4">
                      <div className="w-16 h-16 mx-auto">
                        <img
                          src={user.avatar != null ? backendUrl + user.avatar : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                          alt={user.name}
                          className={`w-full h-full rounded-full object-cover ring-2 transition-all duration-300 ${darkMode
                            ? "ring-white/10 group-hover:ring-pink-400"
                            : "ring-gray-100 group-hover:ring-pink-400"
                            }`}
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="text-center space-y-2">
                      <h4
                        className={`text-lg font-semibold transition-colors duration-300 ${darkMode
                          ? "text-white group-hover:text-white-400"
                          : "text-gray-900 group-hover:text-gray-900"
                          }`}
                      >
                        {user.name}
                      </h4>

                      <p
                        className={`text-sm font-medium ${darkMode ? "text-white/60" : "text-gray-500"
                          }`}
                      >
                        {user.job_title}
                      </p>

                      <div className="flex items-center justify-center">
                        <span
                          className={`text-xs font-medium tracking-wide uppercase ${darkMode ? "text-white/50" : "text-gray-400"
                            }`}
                        >
                          {user.department}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* List View Layout */}
                {viewMode === "list" && (
                  <>
                    {/* Avatar */}
                    <div className="relative mr-5">
                      <div className="w-12 h-12">
                        <img
                          src={user.avatar != null ? backendUrl + user.avatar : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                          alt={user.name}
                          className={`w-full h-full rounded-full object-cover ring-2 transition-all duration-300 ${darkMode
                            ? "ring-white/10 group-hover:ring-pink-400"
                            : "ring-gray-100 group-hover:ring-pink-400"
                            }`}
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Main User Info */}
                    <div className="flex-1 min-w-0">
                      {/* Name */}
                      <h4
                        className={`text-base font-semibold transition-colors duration-300 mb-1 ${darkMode
                          ? "text-white group-hover:text-white-400"
                          : "text-gray-900 group-hover:text-black-600"
                          }`}
                      >
                        {user.name}
                      </h4>

                      {/* Role and Department */}
                      <div className="flex sm:flex-row flex-col sm:items-center items-start sm:gap-2 gap-0">
                        <p
                          className={`text-sm font-medium ${darkMode ? "text-white/70" : "text-gray-600"
                            }`}
                        >
                          {user.job_title}
                        </p>
                        <span
                          className={`text-xs ${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        >
                          •
                        </span>
                        <p
                          className={`text-xs font-medium tracking-wide uppercase ${darkMode ? "text-white/50" : "text-gray-500"
                            }`}
                        >
                          {user.department}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Enhanced Profile Modal - UI Changed to Match New Design */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm overflow-hidden p-4">
            <div
              className={`rounded-3xl shadow-2xl w-full max-w-3xl relative max-h-[90vh] overflow-hidden 
                bg-[#1b1c29] border border-[#3b3c4a] text-white`}
            >
              {/* Modal Container with Custom Dark Background */}
              <div className="p-4 sm:p-6 lg:p-8">
                {/* Header Section */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4 sm:gap-6">
                    {/* Avatar */}
                    <div className="relative">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-3xl  p-[2px] bg-gradient-to-r from-pink-600 to-red-500 ">
                        <img
                          src={selectedUser.avatar != null ? backendUrl + selectedUser.avatar : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                          alt={selectedUser.name}
                          className="w-full h-full rounded-3xl object-cover"
                        />
                      </div>
                    </div>

                    {/* User Info */}
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                        {selectedUser.name}
                      </h2>
                      {/* Role Icon */}
                      <div className="flex items-center gap-3">
                        {selectedUser.job_title && (
                          <span className="flex items-center gap-1 text-xs text-white/80 font-medium">
                            <Briefcase size={12} />
                            {selectedUser.job_title}
                          </span>
                        )}
                        {/* {selectedUser.department && (
                          <span className="flex items-center gap-1 text-xs text-white/80 font-medium">
                            <Building size={12}  />
                            {selectedUser.department}
                          </span>
                        )} */}
                      </div>
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition-all duration-200 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white"
                    aria-label="Close"
                    onClick={() => setSelectedUser(null)}
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Content Area - Scrollable */}
                <div className="mt-8 space-y-6 overflow-y-auto max-h-[65vh] pr-2 custom-scrollbar">
                  {/* Contact/Location Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Email */}
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-white/70">Email</div>
                      <div className="relative">
                        <input
                          type="email"
                          value={selectedUser.email || "N/A"}
                          readOnly
                          className="w-full text-white bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl py-3 pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500"
                        />
                        <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 " />
                      </div>
                    </div>

                    {/* Phone Number */}
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-white/70">Phone Number</div>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedUser.phone_number || "N/A"}
                          readOnly
                          className="w-full text-white bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl py-3 pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500"
                        />
                        <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 " />
                      </div>
                    </div>

                    {/* Department */}
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-white/70">Department</div>
                      <div className="relative">
                        <input
                          type="text"
                          value={selectedUser.department || "N/A"}
                          readOnly
                          className="w-full text-white bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl py-3 pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500"
                        />
                        <Building size={16} className="absolute left-4 top-1/2 -translate-y-1/2 " />
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-white/70">Location</div>
                      <div className="relative">
                        <input
                          type="text"
                          value={`${selectedUser.location || "N/A"}${selectedUser.timezone ? ` (${selectedUser.timezone})` : ""}`}
                          readOnly
                          className="w-full text-white bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl py-3 pl-10 pr-4 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-pink-500"
                        />
                        <MapPin size={16} className="absolute left-4 top-1/2 -translate-y-1/2 " />
                      </div>
                    </div>
                  </div>

                  {/* Bio */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-white/70">Bio</div>
                    <div className="bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl p-4">
                      <p className="text-sm leading-relaxed text-white/80">
                        {selectedUser.bio || "No biography provided."}
                      </p>
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-white/70">Skills & Expertise</div>
                    <div className="bg-[#2a2c3a] border border-[#3b3c4a] rounded-xl p-4">
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.skills && selectedUser.skills.length > 0 ? (
                          selectedUser.skills.map((skill: string, idx: number) => (
                            <span
                              key={idx}
                              className="px-3 py-1 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-pink-600 to-red-500 to-purple-600 border border-white/20 shadow-sm"
                            >                            
                              {skill}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-white/60">No skills listed.</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Custom scrollbar style to match the new design (FIXED) */}
                <style>{`
                  .custom-scrollbar::-webkit-scrollbar {
                    width: 8px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: #4b5563; /* Gray-600 */
                    border-radius: 4px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-track {
                    background-color: #1f2937; /* Gray-800 */
                  }
                `}</style>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Community;